import { useEffect, useRef } from 'react';
import { Formik, useFormikContext, type FormikConfig } from 'formik';
import { useTranslation } from 'react-i18next';
import { ValidationSchemaProvider } from '@/context';

/**
 * Validation messages are resolved when validation runs, so an error already
 * on screen keeps the old language after a language switch. Re-run validation
 * (only when errors are showing) so it follows the new language.
 */
function RevalidateOnLanguageChange() {
  const { i18n } = useTranslation();
  const { errors, validateForm } = useFormikContext();
  const previousLanguage = useRef(i18n.language);
  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    if (previousLanguage.current === i18n.language) return;
    previousLanguage.current = i18n.language;
    if (hasErrors) validateForm();
  }, [i18n.language, hasErrors, validateForm]);

  return null;
}

/**
 * A higher-order component that wraps a Formik component with a ValidationSchemaProvider.
 * It provides the wrapped Formik component with the validationSchema, allowing for automatic validation based on the schema.
 * @param validationSchema - The schema to use for validation.
 * @param children - The children elements to render within the Formik component.
 * @param props - The props to pass to the Formik component.
 * @returns The wrapped Formik component.
 */
export function FormikWithSchema<Values extends object>({
  validationSchema,
  children,
  ...props
}: FormikConfig<Values>) {
  return (
    <ValidationSchemaProvider validationSchema={validationSchema}>
      <Formik {...props} validationSchema={validationSchema}>
        {(formik) => (
          <>
            <RevalidateOnLanguageChange />
            {typeof children === 'function' ? children(formik) : children}
          </>
        )}
      </Formik>
    </ValidationSchemaProvider>
  );
}
